//* <veva-footnote> holds a note where the text references it ([footnote]note[/footnote]); <veva-footnotes> lists the notes before it.
//* A note renders only into its own shadow root, and a list only into its own element, which Blazor renders empty,
//* so neither touches the DOM Blazor keeps. Without script, the note shows in brackets (styles.scss).

const markStyles = `
:host { position: relative; }
sup { line-height: 0; }
.mark { font: inherit; color: var(--veva-footnote-mark-color, inherit); background: none; border: 0; padding: 0 0.1em; margin: 0; cursor: pointer; text-decoration: none; }
.mark:hover, .mark:focus-visible { text-decoration: underline; }
.note {
	position: absolute; left: 0; bottom: 100%; z-index: 1000; box-sizing: border-box;
	width: max-content; max-width: min(24rem, calc(100vw - 1rem)); padding: 0.5em 0.75em; border-radius: 0.25em;
	background: var(--veva-footnote-note-background, Canvas); color: var(--veva-footnote-note-color, CanvasText);
	box-shadow: 0 0.25em 1em rgb(0 0 0 / 0.2); font-size: 0.875rem; font-weight: normal; font-style: normal;
	line-height: 1.4; text-align: start; white-space: normal;
}
.note.below { bottom: auto; top: 100%; }
.note[hidden] { display: none; }
.note a { color: var(--veva-footnote-note-link-color, LinkText); }
`;

const closeDelay = 150;
const viewportMargin = 8;

let pending = false;

function scheduleUpdate() {
	if (pending) return;
	pending = true;
	queueMicrotask(() => {
		pending = false;
		update();
	});
}

//* Notes are numbered in page order; each list shows the notes after the previous list
function update() {
	const notes = Array.from(document.querySelectorAll<FootnoteElement>('veva-footnote')).filter(
		(note) => !note.parentElement?.closest('veva-footnote, veva-footnotes'),
	);
	const lists = Array.from(document.querySelectorAll<FootnotesElement>('veva-footnotes'));
	const listed = new Map<FootnotesElement, FootnoteElement[]>(lists.map((list) => [list, []]));

	notes.forEach((note, index) => {
		const list = lists.find((candidate) => note.compareDocumentPosition(candidate) & Node.DOCUMENT_POSITION_FOLLOWING);
		note.show(index + 1, list ? `veva-footnote-${index + 1}` : null);
		list && listed.get(list)!.push(note);
	});

	listed.forEach((items, list) => list.show(items));
}

function cloneContent(element: Element) {
	return Array.from(element.childNodes, (node) => node.cloneNode(true));
}

export class FootnoteElement extends HTMLElement {
	#root = this.attachShadow({ mode: 'open' });
	#observer = new MutationObserver(scheduleUpdate);
	#closeTimer = 0;
	#shown = '';
	number = 0;

	connectedCallback() {
		this.#observer.observe(this, { childList: true, subtree: true, characterData: true });
		this.addEventListener('pointerenter', this.#open);
		this.addEventListener('pointerleave', this.#closeSoon);
		this.addEventListener('focusin', this.#open);
		this.addEventListener('focusout', this.#closeSoon);
		this.addEventListener('keydown', this.#closeOnEscape);
		scheduleUpdate();
	}

	disconnectedCallback() {
		this.#observer.disconnect();
		scheduleUpdate();
	}

	show(number: number, target: string | null) {
		const shown = `${number}|${target}|${this.innerHTML}`;
		if (shown === this.#shown) return;
		this.#shown = shown;
		this.number = number;

		const style = document.createElement('style');
		style.textContent = markStyles;

		const mark = document.createElement(target ? 'a' : 'button');
		mark.className = 'mark';
		mark.setAttribute('part', 'mark');
		mark.setAttribute('role', 'doc-noteref');
		mark.setAttribute('aria-describedby', 'note');
		mark.textContent = `${number}`;
		//* The click is the note's own: a sort link or a row link around the note must not act on it too
		mark.addEventListener('click', (event) => event.stopPropagation());
		if (target) {
			mark.setAttribute('href', `#${target}`);
		} else {
			mark.setAttribute('type', 'button');
			mark.addEventListener('click', () => (this.#note()?.hidden ? this.#open() : this.#close()));
		}

		const sup = document.createElement('sup');
		sup.append(mark);

		const note = document.createElement('span');
		note.id = 'note';
		note.className = 'note';
		note.setAttribute('part', 'note');
		note.setAttribute('role', 'tooltip');
		note.hidden = true;
		note.append(...cloneContent(this));

		this.#root.replaceChildren(style, sup, note);
	}

	focusMark() {
		this.scrollIntoView({ block: 'center' });
		this.#root.querySelector<HTMLElement>('.mark')?.focus();
	}

	#note() {
		return this.#root.querySelector<HTMLElement>('.note');
	}

	#open = () => {
		clearTimeout(this.#closeTimer);
		const note = this.#note();
		if (!note || !note.hidden) return;

		note.hidden = false;
		note.classList.remove('below');
		note.style.translate = '';

		const rect = note.getBoundingClientRect();
		if (rect.top < viewportMargin) {
			note.classList.add('below');
		}
		const overflow = rect.right - (document.documentElement.clientWidth - viewportMargin);
		const shift = overflow > 0 ? -Math.min(overflow, rect.left - viewportMargin) : 0;
		if (shift) {
			note.style.translate = `${shift}px 0`;
		}
	};

	#close = () => {
		clearTimeout(this.#closeTimer);
		const note = this.#note();
		if (note) note.hidden = true;
	};

	#closeSoon = () => {
		clearTimeout(this.#closeTimer);
		this.#closeTimer = window.setTimeout(this.#close, closeDelay);
	};

	#closeOnEscape = (event: KeyboardEvent) => {
		if (event.key === 'Escape') this.#close();
	};
}

export class FootnotesElement extends HTMLElement {
	static observedAttributes = ['heading', 'placeholder', 'back-label'];

	#shown = '';

	connectedCallback() {
		scheduleUpdate();
	}

	disconnectedCallback() {
		scheduleUpdate();
	}

	attributeChangedCallback() {
		scheduleUpdate();
	}

	show(notes: FootnoteElement[]) {
		const attributes = FootnotesElement.observedAttributes.map((name) => this.getAttribute(name));
		const shown = JSON.stringify([attributes, notes.map((note) => [note.number, note.innerHTML])]);
		if (shown === this.#shown) return;
		this.#shown = shown;

		if (notes.length === 0) {
			const placeholder = this.getAttribute('placeholder');
			if (!placeholder) {
				this.replaceChildren();
				return;
			}

			const text = document.createElement('p');
			text.className = 'veva-footnotes__placeholder';
			text.textContent = placeholder;
			this.replaceChildren(text);
			return;
		}

		const children: Node[] = [];
		const heading = this.getAttribute('heading');
		if (heading) {
			const title = document.createElement('h2');
			title.className = 'veva-footnotes__heading';
			title.textContent = heading;
			children.push(title);
		}

		const list = document.createElement('ol');
		list.className = 'veva-footnotes__list';
		for (const note of notes) {
			const item = document.createElement('li');
			item.id = `veva-footnote-${note.number}`;
			item.value = note.number;
			item.className = 'veva-footnotes__item';
			item.setAttribute('role', 'doc-endnote');

			const back = document.createElement('a');
			back.href = '#';
			back.className = 'veva-footnotes__back';
			back.setAttribute('role', 'doc-backlink');
			back.setAttribute('aria-label', this.getAttribute('back-label') || 'Back to the text');
			back.textContent = '↑';
			back.addEventListener('click', (event) => {
				event.preventDefault();
				note.focusMark();
			});

			const text = document.createElement('span');
			text.className = 'veva-footnotes__text';
			text.append(...cloneContent(note));

			item.append(back, ' ', text);
			list.append(item);
		}
		children.push(list);

		this.replaceChildren(...children);
	}
}

export function defineFootnotes() {
	if (!customElements.get('veva-footnote')) customElements.define('veva-footnote', FootnoteElement);
	if (!customElements.get('veva-footnotes')) customElements.define('veva-footnotes', FootnotesElement);
}
